import { gql, useLazyQuery, useQuery } from '@apollo/client'
import get from 'lodash/get'
import { useState } from 'react'

import { useGetNewestData } from '@/clients/utoniq-core'
import {
  FetchNewMyTradeQuery,
  FetchNewMyTradeQueryVariables,
  MyRequestsQuery,
  MyRequestsQueryVariables,
  MyTradeQuery,
  MyTradeQueryVariables,
  TradeBoxFragmentDoc,
  TradeCandidateNegotiationStatus,
  TradeMyCandidatesConnectionFragmentDoc,
  TradeNegotiationsMyRequestsConnectionFragmentDoc,
  TradeNegotiationStatus,
} from '@/clients/utoniq-core/schema'
import { useCurrentUser } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user: currentUser } = useCurrentUser()
  const params = new URLSearchParams(window.location.search)
  const eventFilter = params.get('filter') ?? 'all'
  const type = params.get('type') ?? 'candidate'

  const candidateTradeStatus = [
    { key: 'all', value: undefined },
    {
      key: 'duringTrading',
      value: [TradeCandidateNegotiationStatus.InProgress],
    },
    {
      key: 'success',
      value: [TradeCandidateNegotiationStatus.Accepted],
    },
    {
      key: 'failure',
      value: [
        TradeCandidateNegotiationStatus.Rejected,
        TradeCandidateNegotiationStatus.Dropped,
        TradeCandidateNegotiationStatus.Expired,
      ],
    },
  ]

  const [isCalledMyTrade, setIsCalledMyTrade] = useState(false)
  const { isFirstLoadRef } = useGetNewestData<
    FetchNewMyTradeQuery,
    FetchNewMyTradeQueryVariables
  >({
    query: FETCH_NEW_QUERY,
    variables: {
      pageInput: { limit: 30 },
      status: candidateTradeStatus.find(element => element.key === eventFilter)
        ?.value,
    },
    fields: [
      {
        fieldName: 'tradeMyCandidates',
        fragment: TradeBoxFragmentDoc,
        uniqByKey: 'node.tradeNegotiation.__ref',
        generateNode(cache, data) {
          return {
            __typename: 'TradeCandidate',
            tradeNegotiation: cache.writeFragment({
              fragment: TradeBoxFragmentDoc,
              data: get(data, 'tradeNegotiation'),
            }),
          }
        },
      },
    ],
  })

  const { data, loading, fetchMore, refetch } = useQuery<
    MyTradeQuery,
    MyTradeQueryVariables
  >(QUERY, {
    returnPartialData: true,
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted() {
      isFirstLoadRef.current = false
    },
  })

  const [
    getTradeMyRequests,
    {
      data: myTradeRequests,
      fetchMore: fetchMoreTradeMyRequest,
      refetch: refetchTradeMyRequests,
      loading: loadingCandidate,
    },
  ] = useLazyQuery<MyRequestsQuery, MyRequestsQueryVariables>(
    ARTIST_TRADE_NEGOTIATIONS_MY_REQUESTS
  )

  return {
    state: {
      loading: loading || loadingCandidate,
      hasNextPageRequest:
        !!myTradeRequests?.tradeNegotiationsMyRequests?.pageInfo?.hasNextPage,
      hasNextPageCandidates: !!data?.tradeMyCandidates?.pageInfo?.hasNextPage,
      tradeRequests: myTradeRequests?.tradeNegotiationsMyRequests ?? null,
      tradeCandidates: data?.tradeMyCandidates ?? null,
      currentUser,
      filter: {
        type: type,
        eventFilter: eventFilter,
      },
    },
    actions: {
      async onFetchMore({ status, type }) {
        if (type === 'candidate') {
          await fetchMore({
            variables: {
              status: status,
              pageInput: {
                cursor: data?.tradeMyCandidates?.pageInfo?.endCursor,
              },
            },
          })
          return
        }
        fetchMoreTradeMyRequest &&
          fetchMoreTradeMyRequest({
            variables: {
              status: status,
              pageInput: {
                cursor:
                  myTradeRequests?.tradeNegotiationsMyRequests?.pageInfo
                    ?.endCursor,
              },
            },
          })
      },
      async onChangeSegment({ type }) {
        if (type === 'candidate') {
          await refetch({ status: undefined })
          return
        }
        if (!isCalledMyTrade) {
          await getTradeMyRequests({ variables: { status: undefined } })
          setIsCalledMyTrade(true)
          return
        }
        await refetchTradeMyRequests({ status: undefined })
      },
      async onChangeStatus({ status, type }) {
        if (type === 'candidate') {
          await refetch({
            status: status as TradeCandidateNegotiationStatus[],
          })
          return
        }
        if (!isCalledMyTrade) {
          await getTradeMyRequests({
            variables: {
              status: status as TradeNegotiationStatus[],
            },
          })
          setIsCalledMyTrade(true)
          return
        }
        await refetchTradeMyRequests({
          status: status as TradeNegotiationStatus[],
        })
      },
    },
  }
})

const QUERY = gql`
  query MyTrade(
    $status: [TradeCandidateNegotiationStatus!]
    $pageInput: PageInput
  ) {
    tradeMyCandidates(status: $status, pageInput: $pageInput) {
      ...TradeMyCandidatesConnection
    }
  }
  ${TradeMyCandidatesConnectionFragmentDoc}
`

const FETCH_NEW_QUERY = gql`
  query FetchNewMyTrade(
    $status: [TradeCandidateNegotiationStatus!]
    $pageInput: PageInput
  ) {
    tradeMyCandidates(status: $status, pageInput: $pageInput) {
      ...TradeMyCandidatesConnection
    }
  }
  ${TradeMyCandidatesConnectionFragmentDoc}
`

const ARTIST_TRADE_NEGOTIATIONS_MY_REQUESTS = gql`
  query MyRequests($status: [TradeNegotiationStatus!], $pageInput: PageInput) {
    tradeNegotiationsMyRequests(status: $status, pageInput: $pageInput) {
      ...TradeNegotiationsMyRequestsConnection
    }
  }
  ${TradeNegotiationsMyRequestsConnectionFragmentDoc}
`
